import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { User } from '@/types/auth'
import client from '@/api/client'
import { probeUserCable, resetUserCable } from '@/cables'
import { useAsyncAction } from '@/composables/useAsyncAction'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const organizationName = ref<string | null>(null)
  const authenticated = ref(false)
  const initialized = ref(false)
  // Se resuelve una vez por sesion: el scope correcto es la sesion, no el usuario
  // ni la organizacion, porque quien bloquea los WebSockets es la red desde la
  // que se conecta (mismo usuario: oficina con proxy = no, casa = si).
  const streamsAvailable = ref(false)
  let streamsProbe: Promise<boolean> | null = null

  const { loading, error, execute } = useAsyncAction()

  const isAuthenticated = computed(() => authenticated.value)
  const isAdmin = computed(() => user.value?.role === 'admin')
  const isGestor = computed(() => user.value?.role === 'gestor')
  const isViewer = computed(() => user.value?.role === 'viewer')
  const pylonHmac = computed(() => user.value?.pylon_hmac ?? null)

  const hasOrganizationAccess = computed(() =>
    user.value?.resource_access === 'organization',
  )

  function setUser(newUser: User) {
    user.value = newUser
    authenticated.value = true
  }

  // Corre la primera vez que alguien va a streamear, no al autenticarse: probar
  // en el login deja un socket abierto por cada usuario logueado para servir un
  // modal que la mayoria no abre nunca. Ademas asi el resultado es de la red en
  // la que el usuario esta ahora y no de la que tenia cuando entro.
  //
  // Se memoiza la promesa, no el valor: llamadas en paralelo comparten el mismo
  // probe. Si falla, el default (false) es el modo que funciona en cualquier red.
  async function ensureStreamsAvailable() {
    streamsProbe ??= probeUserCable().catch(() => false)
    streamsAvailable.value = await streamsProbe
    return streamsAvailable.value
  }

  async function login(email: string, password: string) {
    await execute(async () => {
      const { data } = await client.post<{ user: User }>('/session', {
        email,
        password,
      })
      setUser(data.user)
    })
  }

  async function discover(email: string) {
    return await execute(async () => {
      const { data } = await client.post<{
        auth_method: 'password' | 'authkit' | 'sso'
        authorization_url?: string
      }>('/session/discover', { email })
      return data
    })
  }

  async function fetchCurrentUser() {
    await execute(async () => {
      const { data } = await client.get<User>('/session')
      user.value = data
      authenticated.value = true
    })
  }

  async function checkAuth() {
    try {
      await fetchCurrentUser()
    } catch {
      user.value = null
    } finally {
      initialized.value = true
    }
  }

  function clearSession() {
    user.value = null
    organizationName.value = null
    authenticated.value = false
    streamsAvailable.value = false
    streamsProbe = null
    resetUserCable()
  }

  async function logout() {
    try {
      const { data } = await client.delete<{ logout_url?: string }>('/session')
      clearSession()

      if (data.logout_url) {
        window.location.href = data.logout_url
        return
      }
    } catch {
      clearSession()
    }
  }

  return {
    user,
    organizationName,
    loading,
    error,
    initialized,
    streamsAvailable,
    isAuthenticated,
    isAdmin,
    isGestor,
    isViewer,
    pylonHmac,
    hasOrganizationAccess,
    ensureStreamsAvailable,
    setUser,
    login,
    discover,
    fetchCurrentUser,
    checkAuth,
    logout,
  }
})
